
import { Button, Col, Divider, Row, Spin, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import Icon from "../../../assets/icons/icon";
import { renderErrorMessage } from "../../../components/messages/errorMessage";
import _t from "../../../lang/translate";
import { motorAtlasTransformedSource } from "../../../services/motorAtlasService";
import { ModelMatch, SourceVehiclePreview } from "../../../types/motorAtlasCatalogTypes";
import { printReadableECode } from "../../../utilities/stringExtraFunctions";
import MotorAtlasCatalogMapper from "./motorAtlasCatalogMapper";
import { createEmptyModelMatch, ModalConfig, SubmitPayload } from "./util";
import VehicleDetails from "./vehiclePreview";
import MotorAtlasLink from "../../../components/motorAtlasLink";

type MotorAtlasCatalogMapperProps = {
  submit: (submitPayload: SubmitPayload) => void;
  onCancel: (errors?: any) => void;
  payload: ModalConfig
}



const CAR_API_URL = process.env.REACT_APP_MOTOR_ATLAS_URL

export default function ModalContent({ onCancel, submit, payload }: MotorAtlasCatalogMapperProps) {
  const { inputVehicle, selectedMotorAtlasId, views, presetFilters } = payload
  const presetFiltersRef = useRef(presetFilters)
  const [selectedMatch, setSelectedMatch] = useState<ModelMatch | null>(createEmptyModelMatch(selectedMotorAtlasId))
  const [sourceData, setSourceData] = useState<SourceVehiclePreview | null>(null)
  const [loading, setLoading] = useState(true)


  function handleSubmit() {
    try {
      if (!!selectedMatch) {
        submit({ selectedMatch: selectedMatch, appliedFilters: presetFiltersRef.current })
      } else {
        throw new Error("No model selected")
      }
    } catch (err) {
      renderErrorMessage(err)
    }
  }

  async function fetchSourceVehicle() {
    try {
      setLoading(true)
      let source = inputVehicle?.link ?? ''
      if (inputVehicle?.vin) {
        source = inputVehicle.vin
      }
      if (source) {
        const { data: { data } } = await motorAtlasTransformedSource(source, inputVehicle?.firstRegDate)
        const patchedCore = patchCoreVehicle(data?.core, inputVehicle?.firstRegDate)
        setSourceData({ ...data, core: patchedCore ?? data.core })
      }
    } catch (err) {
      renderErrorMessage(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSourceVehicle()
  }, [])


  return (
    <div>
      <div className="pt-1 pb-1 pl-2 pr-2" style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <h3 className="m-0" >Vælg model</h3>
          {inputVehicle?.motorAtlasId && <MotorAtlasLink motorAtlasId={inputVehicle?.motorAtlasId} firstRegDate={inputVehicle.firstRegDate}
            isVan={inputVehicle.typeId === 2} />}
        </div>
        <Button onClick={onCancel} type="text" icon={<Icon size="huge" name="close-outline" />}></Button>
      </div>
      <Divider className="m-0" />
      <Spin spinning={loading}>

        {!loading ? (
          <Row style={{ padding: "16px 24px" }} gutter={[16, 16]}>
            {!views?.hideSourceVehicle && inputVehicle && (
              <Col span={12}>
                <VehicleDetails sourceVehicle={sourceData} inputVehicle={inputVehicle} />
              </Col>
            )}
            <Col span={inputVehicle ? 12 : 24}>
              <MotorAtlasCatalogMapper
                submit={submit}
                presetFiltersRef={presetFiltersRef}
                inputVehicle={inputVehicle}
                sourceVehicle={sourceData}
                selectedMatch={selectedMatch}
                setSelectedMatch={setSelectedMatch}
              />
            </Col>
          </Row>
        ) : (
          <div>
            <Typography.Title level={4} className="text-center mt-1 mb-1">
              Indlæser vurderingskørejet...
            </Typography.Title>
          </div>
        )}
      </Spin>

      <div>
        <Divider className="m-0" />
        <Row justify="space-between" className="p-2">
          <Col>
            <Button onClick={onCancel}>{_t("cancel")}</Button>
          </Col>
          <Col>
            <Button type="primary" disabled={!selectedMatch} onClick={handleSubmit}>{_t("choose", "model")}</Button>
          </Col>
        </Row>
      </div>
    </div>
  )
}

function patchCoreVehicle(core: SourceVehiclePreview['core'] | undefined, dealerFirstRegDate: string | null | undefined) {
  if (!core) return;
  let firstRegDate = core.firstRegDate;
  if (!firstRegDate) {
    firstRegDate = dealerFirstRegDate ?? ''
  }
  return {
    ...core,
    firstRegDate
  }
}
